// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {
    AfterContentInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren
} from '@angular/core';
import {
    IClientPolicyDefaults,
    ID3EPolicy,
    ID3EPolicyRequest,
    IPolicyExtensions,
    IPolicyFeatureFlags,
    IPolicyFolders,
    IPolicySection,
    IRansomExtensions,
    ISensor,
    ransomwareFeatureMaskValues,
    validTimeoutValues
} from 'src/types/d3ePolicy';
import { D3eService } from 'src/services/d3e-service';
import { NotificationService } from 'src/services/notification-service';
import { ILicense } from 'src/types/subscription';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ConfigurationService } from '../../../../services/configuration-service';
import { MatTab } from '@angular/material/tabs';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-policy-editor',
    templateUrl: './policy-editor.component.html',
    styleUrls: ['./policy-editor.component.scss'],
    standalone: false
})
export class PolicyEditorComponent implements OnChanges, AfterContentInit {
  @ViewChildren(MatTab) policyTabs!: QueryList<MatTab>;
  @Input()d3ePolicy: ID3EPolicy;
  @Input()show_force_policy_checkbox: boolean;
  @Input()show_environment_variables: boolean; 
  @Input()d3ePolicyName: string;
  @Input()d3ePolicyId: number;
  @Input()devices: ILicense[];
  @Input()isDirty: boolean;
  @Input()sensors: ISensor[];
  @Input()policyEnabled: boolean;
  @Input()overrideLocalPolicy: boolean;
  @Input()clientPolicy: IClientPolicyDefaults;
  @Input()clientPolicyDefaults: IClientPolicyDefaults;
  @Input()policyPageConfig: IPolicyFeatureFlags;
  @Input()allowAlwaysOn: boolean;
  @Input()selectedOrgId: string;
  @Input()subKeyUUIDs: string[];
  @Input()enableSubscriptionKey = false;
  @Input()haveDDTier: boolean;
  @Input()haveFDETier: boolean;
  @Input()havePBATier: boolean;
  @Input()haveBitLockerTier: boolean;
  @Input()enableDataDeception: boolean = false;

  @Output()policyCanceled: EventEmitter<void> = new EventEmitter<void>();
  @Output()policySaved: EventEmitter<void> = new EventEmitter<void>();
  @Output()loadDeviceSettings: EventEmitter<ILicense> = new EventEmitter<ILicense>();
  @Output()policyIsDirty: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()overrideLocalPolicyChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()allowAlwaysOnChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()preAuthFileAccessCountChanged: EventEmitter<number> = new EventEmitter<number>();
  @Output()preAuthFileAccessTimeoutChanged: EventEmitter<ID3EPolicy> = new EventEmitter<ID3EPolicy>();

  _d3ePolicy: ID3EPolicy;
  _policyEnabled: boolean;
  defaultPolicyExtensions: IPolicyExtensions;
  customPolicyExtensions: IPolicyExtensions;
  ransomPolicyExtensions: string[];
  policyFolders: IPolicyFolders;
  deceptionFiles: string[];
  selectedTabIndex = 0;
  hasRansomExtensionError = false;
  hasExtensionError = false;
  folderError = false;
  deceptionFileError = false;
  policySections: IPolicySection[] = [];
  importDefaultExts: IPolicyExtensions = {disabledExtensions: [], protectedExtensions: [], lockedExtensions: []};

  fSuccess: string;
  fUpdatingPolicy: string;
  fInvalidCustomTypes: string;
  fInvalidDirectory: string;
  fInvalidDeceptionFile: string;
  fInvalidSafeAppDisplayName: string;
  fInvalidSafeAppCertificate: string;
  fInvalidSafeAppPaths: string;
  fInvalidSafeAppEmptyPathOrFilename: string;

  constructor(
    private dialog: MatDialog,
    private d3eService: D3eService,
    private notification: NotificationService,
    private translate: TranslateService,
    public configurationService: ConfigurationService
  ) {
      this.show_force_policy_checkbox = false;
      this.show_environment_variables = false;
      this.overrideLocalPolicy = false;
      this.policyEnabled = true;
      this._policyEnabled = this.policyEnabled;
      this.allowAlwaysOn = false;
      this.subKeyUUIDs = [];

      this.translate.get(['SUCCESS', 'P_UPDATING_POLICY', 'P_INVALID_CUSTOM_TYPES', 'P_INVALID_DIRECTORY',
      'P_INVALID_DECEPTION_FILE', 'P_INVALID_SAFE_APP_DISPLAY_NAME', 'P_INVALID_SAFE_APP_CERTIFICATE', 'P_INVALID_SAFE_APP_PATHS',
      'P_INVALID_SAFE_APP_EMPTY_PATH_OR_FILENAME']).subscribe(vals => {
        this.fSuccess = vals['SUCCESS'];
        this.fUpdatingPolicy = vals['P_UPDATING_POLICY'];
        this.fInvalidCustomTypes = vals['P_INVALID_CUSTOM_TYPES'];
        this.fInvalidDirectory = vals['P_INVALID_DIRECTORY'];
        this.fInvalidDeceptionFile = vals['P_INVALID_DECEPTION_FILE'];
        this.fInvalidSafeAppDisplayName = vals['P_INVALID_SAFE_APP_DISPLAY_NAME'];
        this.fInvalidSafeAppCertificate = vals['P_INVALID_SAFE_APP_CERTIFICATE'];
        this.fInvalidSafeAppPaths = vals['P_INVALID_SAFE_APP_PATHS'];
        this.fInvalidSafeAppEmptyPathOrFilename = vals['P_INVALID_SAFE_APP_EMPTY_PATH_OR_FILENAME'];
      });
  }

  ngAfterContentInit(): void {
    this.policySections = this.clientPolicy.sections.filter(s => {
      return !['ransomware file types', 'custom file types', 'folders', 'settings', 'known good'].includes(s.name.toLowerCase());
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.d3ePolicyId && changes.d3ePolicyId.previousValue && changes.d3ePolicyId.currentValue) {
      if (changes.d3ePolicyId.previousValue !== changes.d3ePolicyId.currentValue) {
        this.isDirty = false;
      }
    }

    if (changes.d3ePolicyName && changes.d3ePolicyName.currentValue && changes.d3ePolicyName.previousValue) {
      if (changes.d3ePolicyName.previousValue !== changes.d3ePolicyName.currentValue) {
        this.d3ePolicyName = changes.d3ePolicyName.currentValue;
      }
    }

    if (changes.d3ePolicy && changes.d3ePolicy.currentValue) {
      const currentValue = changes.d3ePolicy.currentValue;
      if (Object.keys(currentValue).length > 1) {
        this.updateD3ePolicy(changes.d3ePolicy.currentValue);
      }
    }

    if (changes.overrideLocalPolicy && changes.overrideLocalPolicy.currentValue) {
      this.overrideLocalPolicy = !!changes.overrideLocalPolicy.currentValue;
    }

    if (changes.allowAlwaysOn && changes.allowAlwaysOn.currentValue) {
      this.allowAlwaysOn = !!changes.allowAlwaysOn.currentValue;
    }

  }

  objectIsEmpty(obj): boolean {
    return Object.keys(obj).length === 0;
  }

  getTabIndexByLabel(label: string): number {
    const tabsArray = this.policyTabs.toArray();
    return tabsArray.findIndex(tab => tab.textLabel === label);
  }

  getIcon(name: string) {
    return this.clientPolicy.sections.find(s => s.name === name)?.icon || 'mdi mdi-file-outline';
  }

  getEmptyPolicy() {
    const settings = this.clientPolicyDefaults.sections.find(s => s.name.toLocaleLowerCase() === 'settings').groups[0].settings;
    const defaultSyncInterval = +settings.find(s => s.name.toLowerCase() === 'syncinterval').value;
    const defaultSyncIntervalMetered = +settings.find(s => s.name.toLowerCase() === 'syncintervalmetered').value;
    const defaultLicenseInterval = +settings.find(s => s.name.toLowerCase() === 'licenseinterval').value;
    const defaultLicenseIntervalMetered = +settings.find(s => s.name.toLowerCase() === 'licenseintervalmetered').value;
    const defaultPreAuthFileAccessCount = +settings.find(s => s.name.toLowerCase() === 'preauthfileaccesscount').value;
    const defaultPreAuthFileAccessTimeout: validTimeoutValues =
        +settings.find(s => s.name.toLowerCase() === 'preauthfileaccesstimeout').value as validTimeoutValues;
    const defaultRansomwareSensitivityMin =
        +settings.find(s => s.name.toLowerCase() === 'ransomwaresensitivitymin').value;
    const defaultRansomwareSensitivityMax =
        +settings.find(s => s.name.toLowerCase() === 'ransomwaresensitivitymax').value;
    const defaultRansomwareFeatureMask: ransomwareFeatureMaskValues =
        +settings.find(s => s.name.toLowerCase() === 'ransomwarefeaturemask').value as ransomwareFeatureMaskValues;

    return {
      disabledExts: [],
      protectedExts: [],
      lockedExts: [],
      disabledDirs: [],
      protectedDirs: [],
      lockedDirs: [],
      encryptedProtectedDirs: [],
      encryptedLockedDirs: [],
      deceptionFiles: [],
      protectedVolumes: [],
      sensors: [],
      syncInterval: defaultSyncInterval,
      syncIntervalMetered: defaultSyncIntervalMetered,
      licenseInterval: defaultLicenseInterval,
      licenseIntervalMetered: defaultLicenseIntervalMetered,
      preAuthFileAccessCount: defaultPreAuthFileAccessCount,
      preAuthFileAccessTimeout: defaultPreAuthFileAccessTimeout,
      ransomwareSensitivityMin: defaultRansomwareSensitivityMin,
      ransomwareSensitivityMax: defaultRansomwareSensitivityMax,
      ransomwareFeatureMask: defaultRansomwareFeatureMask,
      knownGood: []
    };
  }

  updateD3ePolicy(d3ePolicy: ID3EPolicy) {
    if (Object.keys(d3ePolicy).length <= 1) {
      d3ePolicy = this.getEmptyPolicy();
    }
    this._d3ePolicy =  Object.assign({}, d3ePolicy); 
    this.policyEnabled = this._policyEnabled;
    if (this.clientPolicy.sections) {
      this.clientPolicy.sections.forEach(g => {
        if (g.groups) {
          g.groups.forEach(f => {
            if (f.extensions) {
              f.extensions.forEach(e => {
                const d3eIsEmpty = this.objectIsEmpty(this._d3ePolicy);
                if (d3eIsEmpty) {
                  e.value = 'none';
                  
                } else {
                  const d3eHasDisabledExts = this._d3ePolicy.disabledExts && this._d3ePolicy.disabledExts.includes(e.extension) || false;
                  const d3eHasProtectedExts = this._d3ePolicy.protectedExts.includes(e.extension);
                  const d3eHasLockedExts = this._d3ePolicy.lockedExts.includes(e.extension);
                  if (d3eHasDisabledExts) {
                    e.value = 'none';
                  } else if (d3eHasProtectedExts) {
                    e.value = 'medium';
                  } else if (d3eHasLockedExts) {
                    e.value = 'high';
                  } else {
                    e.value = 'none';
                  }
                }
              });
            }
          });
        }
      });
    }

    // Flatten the default extensions into a single string array
    const defaultExtensions = this.clientPolicy.sections.reduce(
      (arr, elem) => {
        if (elem.groups) {
          for (const file of elem.groups) {
            if (file.extensions) {
              arr.push(...file.extensions.map(e => e.extension));
            }
          }
        }
        return arr;
      }, [] as string[]
    );

    this.defaultPolicyExtensions = {
      'disabledExtensions': this._d3ePolicy.disabledExts && this._d3ePolicy.disabledExts.filter(e => 
        defaultExtensions.includes(e)) || [],
      'protectedExtensions': this._d3ePolicy.protectedExts && this._d3ePolicy.protectedExts.filter(e => 
        defaultExtensions.includes(e)) || [],
      'lockedExtensions': this._d3ePolicy.lockedExts && this._d3ePolicy.lockedExts.filter(e => 
        defaultExtensions.includes(e)) || []
    };

    this.customPolicyExtensions = {
      'disabledExtensions': this._d3ePolicy.disabledExts && this._d3ePolicy.disabledExts.filter(e => 
        !defaultExtensions.includes(e)) || [],
      'protectedExtensions': this._d3ePolicy.protectedExts && this._d3ePolicy.protectedExts.filter(e => 
        !defaultExtensions.includes(e)) || [],
      'lockedExtensions': this._d3ePolicy.lockedExts && this._d3ePolicy.lockedExts.filter(e => 
        !defaultExtensions.includes(e)) || []
    };

    this.policyFolders = {
      'disabledFolders': this._d3ePolicy.disabledDirs || [],
      'protectedFolders': this._d3ePolicy.protectedDirs || [],
      'lockedFolders': this._d3ePolicy.lockedDirs || [],
      'encryptedProtectedFolders': this._d3ePolicy.encryptedProtectedDirs || [],
      'encryptedLockedFolders': this._d3ePolicy.encryptedLockedDirs || []
    };

    this.deceptionFiles = this._d3ePolicy.deceptionFiles || [];

  }

  onSelectedIndexChange(tabIndex) {
    this.selectedTabIndex = tabIndex;
  }

  onPolicyEnableToggleChange(isEnabled: boolean) {
    this.isDirty = true;
    this.policyEnabled = isEnabled;
    this.policyIsDirty.emit(true);
  }
  
  onForcePolicyRemovalChanged(isEnabled: boolean) {
    this.isDirty = true;
    this.overrideLocalPolicy = isEnabled;
    this.overrideLocalPolicyChanged.emit(isEnabled);
    // this.policyIsDirty.emit(true);
  }

  onAllowAlwaysOnChanged(isEnabled: boolean) {
    this.isDirty = true;
    this.allowAlwaysOn = isEnabled;
    this.allowAlwaysOnChanged.emit(isEnabled);
  }

  onForcePreAuthChanged(d3ePolicy: ID3EPolicy) {
    this._d3ePolicy = d3ePolicy;
    this.isDirty = true;
    this.policyIsDirty.emit(true);
  }

  onPolicySettingChanged(d3ePolicy: ID3EPolicy) {
    this._d3ePolicy = d3ePolicy;
    this.isDirty = true;
    this.policyIsDirty.emit(true);
  }

  onPolicyFoldersChanged(event: IPolicyFolders) {
    this.isDirty = true;
    this.policyIsDirty.emit(true);
    this._d3ePolicy.disabledDirs = event.disabledFolders || [];
    this._d3ePolicy.protectedDirs = event.protectedFolders || [];
    this._d3ePolicy.lockedDirs = event.lockedFolders || [];
    this._d3ePolicy.encryptedProtectedDirs = event.encryptedProtectedFolders || [];
    this._d3ePolicy.encryptedLockedDirs = event.encryptedLockedFolders || [];
  }

  onDeceptionFilesChanged(deceptionFiles: string[]) {
    this.isDirty = true;
    this.policyIsDirty.emit(true);
    this._d3ePolicy.deceptionFiles = deceptionFiles || [];
    // this._d3ePolicy.disabledDirs = event.disabledFolders || [];
    // this._d3ePolicy.protectedDirs = event.protectedFolders || [];
    // this._d3ePolicy.lockedDirs = event.lockedFolders || [];
  }

  onRansomExtensionsChanged(event: IRansomExtensions) {
    this.isDirty = true;
    this.policyIsDirty.emit(true);

    this.ransomPolicyExtensions = event.ransomExtensions;

    this._d3ePolicy.ransomExts = event.ransomExtensions;

  }

  onCustomExtensionsChanged(event: IPolicyExtensions) {
    this.isDirty = true;
    this.policyIsDirty.emit(true);

    const updatedExtensions = {
      'disabledExtensions': event.disabledExtensions || [],
      'protectedExtensions': event.protectedExtensions || [],
      'lockedExtensions': event.lockedExtensions || []
    };

    const defaultExtensions = this.clientPolicy.sections.reduce(
      (arr, elem) => {
        if (elem.groups) {
          for (const file of elem.groups) {
            if (file.extensions) {
              arr.push(...file.extensions.map(e => e.extension));
            }
          }
        }
        return arr;
      }, [] as string[]
    );

    this.customPolicyExtensions = {
      'disabledExtensions': updatedExtensions.disabledExtensions.filter(e => 
        !defaultExtensions.includes(e)) || [],
      'protectedExtensions': updatedExtensions.protectedExtensions.filter(e => 
        !defaultExtensions.includes(e)) || [],
      'lockedExtensions': updatedExtensions.lockedExtensions.filter(e => 
        !defaultExtensions.includes(e)) || []
    };

    this._d3ePolicy.disabledExts = updatedExtensions.disabledExtensions;
    this._d3ePolicy.protectedExts = updatedExtensions.protectedExtensions;
    this._d3ePolicy.lockedExts = updatedExtensions.lockedExtensions;

  }
  
  onImportDefaultExtensionsChanged(event: IPolicyExtensions) {
    this.importDefaultExts = event;
  }

  onLoadDeviceSettings(device) {
    this.isDirty = true;
    this.loadDeviceSettings.emit(device);
    this.policyIsDirty.emit(true);
  }

  onCancel() {
    this.isDirty = false;
    this.hasExtensionError = false;
    this.folderError = false;
    this.deceptionFileError = false;
    this.policyCanceled.emit();
  }

  onIntervalsChanged(d3ePolicy: ID3EPolicy) {
    this.isDirty = true;
    this.policyIsDirty.emit(true);
    this._d3ePolicy.syncInterval = d3ePolicy.syncInterval;
    this._d3ePolicy.syncIntervalMetered = d3ePolicy.syncIntervalMetered;
    this._d3ePolicy.licenseInterval = d3ePolicy.licenseInterval;
    this._d3ePolicy.licenseIntervalMetered = d3ePolicy.licenseIntervalMetered;
  }

  onBitlockerChanged() {
    this.isDirty = true;
    this.policyIsDirty.emit(true);
  }

  onPbaSettingsChanged() {
    this.isDirty = true;
    this.policyIsDirty.emit(true);
  }

  onFdeSettingsChanged() {
    this.isDirty = true;
    this.policyIsDirty.emit(true);
  }

  onSave() {
    if (!this.allowAlwaysOn && 
        (this.defaultPolicyExtensions.lockedExtensions.length > 0 ||
        this.customPolicyExtensions.lockedExtensions.length > 0)
    ) {
      const lockedExtensions = [
        ...this.defaultPolicyExtensions.lockedExtensions, ...this.customPolicyExtensions.lockedExtensions
      ].join(', ');

      const vals = this.translate.instant(['ALWAYS_ON_FOUND_TITLE', 'ALWAYS_ON_FOUND_CONTENT', 'ALERT_OK'], {
        lockedExtensions: lockedExtensions
      });

      const title = vals['ALWAYS_ON_FOUND_TITLE'];
      const content = vals['ALWAYS_ON_FOUND_CONTENT'];
      this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title,
          content,
          acceptBtnName: vals['ALERT_OK'],
          showRejectBtn: false,
        },
      });
      return;
    }

    if (this.hasRansomExtensionError) {
      this.notification.showWarning(this.translate.instant('policy.ransomware.invalid-ext'));
      this.selectedTabIndex = this.getTabIndexByLabel(this.translate.instant('FILE_TYPES'));
      return;
    }

    if (this.hasExtensionError) {
      this.notification.showWarning(this.fInvalidCustomTypes);
      this.selectedTabIndex = this.getTabIndexByLabel(this.translate.instant('FILE_TYPES'));
      return;
    }

    if (this.folderError) {
      this.notification.showWarning(this.fInvalidDirectory);
      this.selectedTabIndex = this.getTabIndexByLabel(this.translate.instant('FOLDERS'));
      return;
    }

    if (this.deceptionFileError) {
      this.notification.showWarning(this.fInvalidDeceptionFile);
      this.selectedTabIndex = this.getTabIndexByLabel(this.translate.instant('DECEPTION'));
      return;
    }

    let safeAppErrors = false;
    for (let i = 0; i < this._d3ePolicy.knownGood.length; ++i) {
      const app = this._d3ePolicy.knownGood[i];
      if (app.displayName.length === 0) {
        this.notification.showWarning(this.fInvalidSafeAppDisplayName);
        safeAppErrors = true;
        break;
      }
      if (!app.subject.CN || app.subject.CN.length === 0) {
        this.notification.showWarning(`${this.fInvalidSafeAppCertificate} ("${app.displayName}").`);
        safeAppErrors = true;
        break;
      }
      if (app.paths.length === 0) {
        this.notification.showWarning(`${this.fInvalidSafeAppPaths} ("${app.displayName}").`);
        safeAppErrors = true;
        break;
      }
      let badPath = false;
      for (let j = 0; j < app.paths.length; ++j) {
        const path = app.paths[j];
        if (path.length === 0) {
          this.notification.showWarning(`${this.fInvalidSafeAppEmptyPathOrFilename} ("${app.displayName}").`);
          safeAppErrors = true;
          badPath = true;
          break;
        }
      }
      if (badPath) {
        break;
      }
    }
    if (safeAppErrors) {
      this.selectedTabIndex = this.getTabIndexByLabel(this.translate.instant('SAFE_APPS'));
      return;
    }

    this.notification.showUpdate(this.fUpdatingPolicy);
    const defaultExtensions = this.clientPolicy.sections.reduce(
      (arr, elem) => {
        if (elem.groups) {
          for (const file of elem.groups) {
            if (file.extensions) {
              arr.push(...file.extensions.map(e => e.extension));
            }
          }
        }
        return arr;
      }, [] as string[]
    );

    this._d3ePolicy.disabledExts = this._d3ePolicy.disabledExts && this._d3ePolicy.disabledExts.filter(e => 
      !defaultExtensions.includes(e)) || [];

    this._d3ePolicy.protectedExts = this._d3ePolicy.protectedExts && this._d3ePolicy.protectedExts.filter(e => 
      !defaultExtensions.includes(e)) || [];
    
    this._d3ePolicy.lockedExts = this._d3ePolicy.lockedExts && this._d3ePolicy.lockedExts.filter(e => 
      !defaultExtensions.includes(e)) || [];

    this._d3ePolicy.disabledExts = [...this.defaultPolicyExtensions.disabledExtensions || [], ...this._d3ePolicy.disabledExts];
    this._d3ePolicy.protectedExts = [...this.defaultPolicyExtensions.protectedExtensions, ...this._d3ePolicy.protectedExts];
    this._d3ePolicy.lockedExts = [...this.defaultPolicyExtensions.lockedExtensions, ...this._d3ePolicy.lockedExts];

    // Don't allow the same extension in multiple protection levels (disabled, protected, and locked)
    // In case of multiple extensions, keep the one that has the highest protection level (locked -> protected -> disabled)
    this._d3ePolicy.disabledExts = this._d3ePolicy.disabledExts.filter(d => {
      return ![...this._d3ePolicy.lockedExts, ...this._d3ePolicy.protectedExts].includes(d);
    });

    this._d3ePolicy.protectedExts = this._d3ePolicy.protectedExts.filter(d => {
      return !this._d3ePolicy.lockedExts.includes(d);
    });
    this._d3ePolicy.knownGood = this.d3ePolicy.knownGood;

    const settings = this.clientPolicyDefaults.sections.find(s => s.name.toLocaleLowerCase() === 'settings').groups[0].settings;
    const ransomwareSensitivityMin = settings.find(s => s.name.toLowerCase() === 'ransomwaresensitivitymin');
    const ransomwareSensitivityMinDefault = ransomwareSensitivityMin.value as number;
    this._d3ePolicy.ransomwareSensitivityMin =
        this._d3ePolicy.ransomwareSensitivityMin ? this._d3ePolicy.ransomwareSensitivityMin : ransomwareSensitivityMinDefault;

    const ransomwareSensitivityMax = settings.find(s => s.name.toLowerCase() === 'ransomwaresensitivitymax');
    const ransomwareSensitivityMaxDefault = ransomwareSensitivityMax.value as number;
    this._d3ePolicy.ransomwareSensitivityMax =
        this._d3ePolicy.ransomwareSensitivityMax ? this._d3ePolicy.ransomwareSensitivityMax : ransomwareSensitivityMaxDefault;

    const ransomwareFeatureMask = settings.find(s => s.name.toLowerCase() === 'ransomwarefeaturemask');
    const ransomwareFeatureMaskDefault = ransomwareFeatureMask.value as ransomwareFeatureMaskValues;
    this._d3ePolicy.ransomwareFeatureMask =
        this._d3ePolicy.ransomwareFeatureMask != null ? this._d3ePolicy.ransomwareFeatureMask : ransomwareFeatureMaskDefault;

    this._d3ePolicy.bitlocker = this.d3ePolicy.bitlocker;

    this._d3ePolicy.pbaSettings = this.d3ePolicy.pbaSettings;

    this._d3ePolicy.fdeSettings = this.d3ePolicy.fdeSettings;

    const policyRequest: ID3EPolicyRequest = {
      name: this.d3ePolicyName,
      policy: {
        // de-dupe picks first occurrence as the one to use
        disabledExts: [...new Set(this._d3ePolicy.disabledExts) || []] as string[],
        protectedExts: [...new Set(this._d3ePolicy.protectedExts)] as string[],
        lockedExts: [...new Set(this._d3ePolicy.lockedExts)] as string[],
        disabledDirs:  this._d3ePolicy.disabledDirs && this._d3ePolicy.disabledDirs.reduce(this.dedupeReducer, []) || [],
        protectedDirs: this._d3ePolicy.protectedDirs.reduce(this.dedupeReducer, []),
        lockedDirs: this._d3ePolicy.lockedDirs.reduce(this.dedupeReducer, []),
        encryptedProtectedDirs: this._d3ePolicy.encryptedProtectedDirs.reduce(this.dedupeReducer, []),
        encryptedLockedDirs: this._d3ePolicy.encryptedLockedDirs.reduce(this.dedupeReducer, []),
        deceptionFiles: this._d3ePolicy.deceptionFiles.reduce(this.dedupeReducer, []) || [],
        syncInterval: this._d3ePolicy.syncInterval,
        syncIntervalMetered: this._d3ePolicy.syncIntervalMetered,
        licenseInterval: this._d3ePolicy.licenseInterval,
        licenseIntervalMetered: this._d3ePolicy.licenseIntervalMetered,
        sensors: this._d3ePolicy.sensors,
        knownGood: this._d3ePolicy.knownGood,
        forcePreAuthValues: this._d3ePolicy.forcePreAuthValues,
        preAuthFileAccessCount: this._d3ePolicy.preAuthFileAccessCount,
        preAuthFileAccessTimeout: this._d3ePolicy.preAuthFileAccessTimeout,
        ransomwareSensitivityMin: this._d3ePolicy.ransomwareSensitivityMin,
        ransomwareSensitivityMax: this._d3ePolicy.ransomwareSensitivityMax,
        ransomwareFeatureMask: this._d3ePolicy.ransomwareFeatureMask,
        bitlocker: this._d3ePolicy.bitlocker,
        pbaSettings: this._d3ePolicy.pbaSettings,
        fdeSettings: this._d3ePolicy.fdeSettings
      },
      enabled: this.policyEnabled,
      overrideLocalPolicy: this.show_force_policy_checkbox ? !!this.overrideLocalPolicy : false,
      allowAlwaysOn: this.allowAlwaysOn,
    };

    if (this?._d3ePolicy.ransomExts != null) {
      policyRequest.policy['ransomExts'] = this._d3ePolicy.ransomExts.reduce(this.dedupeReducer, []);
    }

    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.d3eService
      .setGlobalD3EPolicy(this.d3ePolicyId, policyRequest, selectedOrg)
      .subscribe(
        () => {},
        () => {
          this.notification.showError(this.translate.instant('ERROR_UPDATING_POLICY'));
        },
        () => {
          this.d3ePolicy = Object.assign({}, this._d3ePolicy);
          this.isDirty = false;
          this.policySaved.emit();
          this._policyEnabled = this.policyEnabled;
          this.notification.showSuccess(this.fSuccess);
        }
      );
  }

  onRansomExtErrorStateChanged(errorState: boolean) {
    this.hasRansomExtensionError = errorState;
  }


  onExtensionErrorStateChanged(errorState: boolean) {
    this.hasExtensionError = errorState;
  }

  onFolderErrorStateChanged(errorState: boolean) {
    this.folderError = errorState;
  }

  onDeceptionFileErrorStateChanged(errorState: boolean) {
    this.deceptionFileError = errorState;
  }

  onKnownGoodAppsChanged() {
    this.isDirty = true;
    this.policyIsDirty.emit(true);
  }

  onDefaultExtensionsChanged(event: IPolicyExtensions) {
    this.isDirty = true;
    this.policyIsDirty.emit(true);

    this.defaultPolicyExtensions = {
      'disabledExtensions': [...new Set(event.disabledExtensions ?? [])],
      'protectedExtensions': [...new Set(event.protectedExtensions ?? [])],
      'lockedExtensions': [...new Set(event.lockedExtensions ?? [])]
    };

  }

  dedupeReducer(result, element) {
    const normalize = x => typeof x === 'string' ? x.toLowerCase() : x;
    const normalizedElement = normalize(element);
    if (result.every(otherElement => normalize(otherElement) !== normalizedElement)) {
      result.push(element);
    }
    return result;
  }

}
